import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import RotatingSquare from '../../../svgs/RotatingSquare';
import { useOutsideAlerter } from '../../transcript/useClickOutside';

import type { FormEvent } from 'react';

// Helper to convert language codes to display names
const getLanguageDisplayName = (
  code: string | undefined,
): string | undefined => {
  if (!code) return undefined;

  const languageMap: Record<string, string> = {
    'en-us': 'English (US)',
    'en-gb': 'English (UK)',
    es: 'Spanish',
    'es-mx': 'Spanish (Mexico)',
    fr: 'French',
    de: 'German',
    it: 'Italian',
    pt: 'Portuguese',
    'pt-br': 'Portuguese (Brazil)',
    ja: 'Japanese',
    ko: 'Korean',
    zh: 'Chinese',
    'zh-cn': 'Chinese (Simplified)',
    'zh-tw': 'Chinese (Traditional)',
    ar: 'Arabic',
    ru: 'Russian',
    hi: 'Hindi',
    nl: 'Dutch',
    sv: 'Swedish',
    da: 'Danish',
    no: 'Norwegian',
    fi: 'Finnish',
    pl: 'Polish',
    tr: 'Turkish',
  };

  return languageMap[code.toLowerCase()] || code;
};

type Message = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  versionId?: string;
  versionLabel?: string;
  generationDetails?: GenerationDetails;
  requestedFromVersionId?: string;
  requestedFromVersionLabel?: string;
};

type GenerationDetails = {
  templateName?: string;
  language?: string;
  speakers?: string[];
  goal?: string;
  audiences?: string[];
};

type Speaker = {
  speakerLabel: string;
  name?: string | null;
  title?: string | null;
  bio?: string | null;
  voice?: string | null;
  isAnonymous: boolean;
};

type VoiceOption = 'brand' | Speaker;

type Props = {
  onSendMessage: (
    message: string,
    options: {
      selectedLanguage?: string | null;
      voiceOption?: VoiceOption;
    },
  ) => void;
  isLoading?: boolean;
  selectedLanguage?: string | null;
  latestVersionId?: string | null;
  latestVersionLabel?: string | null;
  onSelectVersion?: (versionId: string) => void;
  generationDetails?: GenerationDetails | null;
  speakers?: Speaker[];
  contentId?: string | number | null; // ID of the currently loaded content - chat resets when this changes
  disableAutoFocus?: boolean; // Suppress auto-focus and auto-scroll (e.g., when loading from table)
  currentWordCount?: number; // Current article word count — shown as context for length revision requests
  currentCharCount?: number; // Current article character count
};

const ChatInterface = ({
  onSendMessage,
  isLoading,
  selectedLanguage,
  latestVersionId,
  latestVersionLabel,
  onSelectVersion,
  generationDetails,
  speakers = [],
  contentId,
  disableAutoFocus,
  currentWordCount,
  currentCharCount,
}: Props) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [wasLoading, setWasLoading] = useState(false);
  // Always default to brand voice, even when a speaker is selected from the Generate modal
  const [selectedVoice, setSelectedVoice] = useState<VoiceOption>('brand');
  const [hasUserSelectedVoice, setHasUserSelectedVoice] = useState(false);
  const [isVoiceDropdownOpen, setIsVoiceDropdownOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Allow sending if user has explicitly selected any voice option
  const hasVoiceChanged = hasUserSelectedVoice;

  // Close dropdown when clicking outside
  const closeVoiceDropdown = useCallback(
    () => setIsVoiceDropdownOpen(false),
    [],
  );
  useOutsideAlerter(dropdownRef, closeVoiceDropdown);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSuggestionClick = (text: string) => {
    setInputValue(text);
    scrollToBottom();
    setTimeout(() => {
      textareaRef.current?.focus();
    }, 0);
  };

  // Track initial mount to skip auto-scroll when opened from table
  const hasInteractedRef = useRef(!disableAutoFocus);

  useEffect(() => {
    // Always scroll when loading starts (shows the generating indicator)
    // or when the user has interacted (sent a message)
    if (isLoading || hasInteractedRef.current) {
      scrollToBottom();
    }
  }, [messages, isLoading]);

  // Mark as interacted once user sends their first message
  useEffect(() => {
    if (messages.length > 0 && messages[messages.length - 1].role === 'user') {
      hasInteractedRef.current = true;
    }
  }, [messages]);

  // Track the last version ID we showed to avoid duplicates
  const lastShownVersionIdRef = useRef<string | null>(null);

  // Add initial generation message when first version is created or when loading from history
  useEffect(() => {
    if (
      latestVersionId &&
      latestVersionLabel &&
      latestVersionId !== lastShownVersionIdRef.current
    ) {
      // Only create initial message if there are no messages yet
      const isFirstVersion = messages.length === 0;

      if (isFirstVersion) {
        // Only include generation details if they have meaningful content
        const hasGenerationDetails =
          generationDetails &&
          (generationDetails.templateName ||
            generationDetails.language ||
            (generationDetails.speakers &&
              generationDetails.speakers.length > 0) ||
            generationDetails.goal ||
            (generationDetails.audiences &&
              generationDetails.audiences.length > 0));

        const initialMessage: Message = {
          id: `initial-generation-${latestVersionId}`,
          role: 'assistant',
          content: '',
          versionId: latestVersionId,
          versionLabel: latestVersionLabel,
          generationDetails: hasGenerationDetails
            ? generationDetails
            : undefined,
        };
        setMessages([initialMessage]);
        lastShownVersionIdRef.current = latestVersionId;
      }
    }
  }, [latestVersionId, latestVersionLabel, generationDetails, messages.length]);

  // Track the version that was showing when we started loading
  const versionIdWhenStartedRef = useRef<string | null>(null);

  // Reset chat when a different content is loaded
  const previousContentIdRef = useRef<string | number | null | undefined>(
    contentId,
  );
  // Track the last version we showed a completion message for (used to prevent duplicates)
  const lastCompletionVersionIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (contentId !== undefined && contentId !== previousContentIdRef.current) {
      // Content changed - reset the chat
      setMessages([]);
      setInputValue('');
      setWasLoading(false);
      setSelectedVoice('brand'); // Always reset to brand voice when content changes
      setHasUserSelectedVoice(false);
      lastShownVersionIdRef.current = null;
      versionIdWhenStartedRef.current = null;
      lastCompletionVersionIdRef.current = null;
      previousContentIdRef.current = contentId;
    }
  }, [contentId]);

  // Track if we're waiting for a new version after loading completes
  const waitingForVersionRef = useRef(false);

  // When loading starts, capture the current version ID
  useEffect(() => {
    if (isLoading && !wasLoading) {
      versionIdWhenStartedRef.current = latestVersionId ?? null;
    }
  }, [isLoading, wasLoading]);

  // When loading finishes, mark that we're waiting for a new version and refocus the input
  useEffect(() => {
    if (wasLoading && !isLoading) {
      const hasUserMessages = messages.some(m => m.role === 'user');
      if (hasUserMessages) {
        waitingForVersionRef.current = true;
      }
      // Return focus to the input after generation completes
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);
    }

    setWasLoading(isLoading ?? false);
  }, [isLoading, wasLoading, messages]);

  // Add completion message when version updates after loading completes
  useEffect(() => {
    if (!waitingForVersionRef.current) return;
    if (!latestVersionId || !latestVersionLabel) return;

    // Check if version ID changed from when we started
    const versionChanged = versionIdWhenStartedRef.current !== latestVersionId;

    if (versionChanged) {
      // Check if we already added a completion message for this version
      // Use a dedicated ref to track this more reliably than checking lastMessage
      const alreadyAddedCompletion =
        lastCompletionVersionIdRef.current === latestVersionId;

      if (!alreadyAddedCompletion) {
        // Add Arbor's response with version reference
        const assistantMessage: Message = {
          id: Date.now().toString(),
          role: 'assistant',
          content: `Done! I've created ${latestVersionLabel}. Let me know if you'd like any other changes.`,
          versionId: latestVersionId,
          versionLabel: latestVersionLabel,
        };
        setMessages(prev => [...prev, assistantMessage]);
        lastCompletionVersionIdRef.current = latestVersionId;
      }

      waitingForVersionRef.current = false;
    }
  }, [latestVersionId, latestVersionLabel]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const hasMessage = inputValue.trim();

    // Allow submission if there's a message OR if voice has changed
    if ((!hasMessage && !hasVoiceChanged) || isLoading) return;

    // Use a default message if only voice changed
    const messageContent = hasMessage
      ? inputValue.trim()
      : `Rewrite using ${
          selectedVoice === 'brand'
            ? 'brand voice'
            : ((selectedVoice as Speaker).name ||
                (selectedVoice as Speaker).speakerLabel) + "'s voice"
        }`;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: messageContent,
      requestedFromVersionId: latestVersionId ?? undefined,
      requestedFromVersionLabel: latestVersionLabel ?? undefined,
    };

    setMessages(prev => [...prev, userMessage]);
    onSendMessage(messageContent, {
      selectedLanguage,
      voiceOption: selectedVoice,
    });
    setInputValue('');
    setHasUserSelectedVoice(false);

    if (textareaRef.current) {
      textareaRef.current.style.height = '40px';
    }

    // Use setTimeout to ensure focus happens after React re-render
    setTimeout(() => {
      textareaRef.current?.focus();
    }, 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
  };

  return (
    <Container data-testid="article-chat-container">
      <Header>
        <HeaderTitle>Revise Content</HeaderTitle>
        <HeaderSubtitle>Tell me what to change</HeaderSubtitle>
      </Header>

      <MessagesContainer>
        <SuggestionsHeader>
          <SuggestionsText>
            Describe what you'd like to change about the content. For example:
          </SuggestionsText>
          {currentWordCount != null && currentWordCount > 0 && (
            <WordCountBadge>
              {currentWordCount} words
              {currentCharCount != null &&
                currentCharCount > 0 &&
                ` · ${currentCharCount} chars`}
            </WordCountBadge>
          )}
          <SuggestionList>
            <Suggestion
              onClick={() => handleSuggestionClick('Make it shorter')}
            >
              Make it shorter
            </Suggestion>
            <Suggestion
              onClick={() => handleSuggestionClick('Add more detail about...')}
            >
              Add more detail about...
            </Suggestion>
            <Suggestion
              onClick={() =>
                handleSuggestionClick('Change the tone to be more formal')
              }
            >
              Change the tone to be more formal
            </Suggestion>
            <Suggestion
              onClick={() => handleSuggestionClick('Focus more on...')}
            >
              Focus more on...
            </Suggestion>
          </SuggestionList>
        </SuggestionsHeader>

        {messages.map(message =>
          message.role === 'user' ? (
            <UserMessageContainer key={message.id}>
              <MessageBubble>{message.content}</MessageBubble>
              {message.requestedFromVersionId &&
                message.requestedFromVersionLabel &&
                onSelectVersion && (
                  <UserMessageVersionLink
                    onClick={() =>
                      onSelectVersion(message.requestedFromVersionId!)
                    }
                  >
                    on {message.requestedFromVersionLabel}
                  </UserMessageVersionLink>
                )}
            </UserMessageContainer>
          ) : (
            <AssistantMessage key={message.id}>
              {message.generationDetails ? (
                <GenerationSummary>
                  <InitialGenerationMessage>
                    Done! I've created{' '}
                    <VersionLink
                      onClick={() => onSelectVersion?.(message.versionId!)}
                    >
                      {message.versionLabel}
                    </VersionLink>
                    . Let me know if you'd like any other changes.
                  </InitialGenerationMessage>
                  <GenerationOptions>
                    {message.generationDetails.templateName && (
                      <OptionItem>
                        <OptionLabel>Theme:</OptionLabel>
                        <OptionValue>
                          {message.generationDetails.templateName}
                        </OptionValue>
                      </OptionItem>
                    )}
                    {message.generationDetails.language && (
                      <OptionItem>
                        <OptionLabel>Language:</OptionLabel>
                        <OptionValue>
                          {getLanguageDisplayName(
                            message.generationDetails.language,
                          )}
                        </OptionValue>
                      </OptionItem>
                    )}
                    {message.generationDetails.speakers &&
                      message.generationDetails.speakers.length > 0 && (
                        <OptionItem>
                          <OptionLabel>Speakers:</OptionLabel>
                          <OptionValue>
                            {message.generationDetails.speakers.join(', ')}
                          </OptionValue>
                        </OptionItem>
                      )}
                    {message.generationDetails.goal && (
                      <OptionItem>
                        <OptionLabel>Goal:</OptionLabel>
                        <OptionValue>
                          {message.generationDetails.goal}
                        </OptionValue>
                      </OptionItem>
                    )}
                    {message.generationDetails.audiences &&
                      message.generationDetails.audiences.length > 0 && (
                        <OptionItem>
                          <OptionLabel>Audiences:</OptionLabel>
                          <OptionValue>
                            {message.generationDetails.audiences.join(', ')}
                          </OptionValue>
                        </OptionItem>
                      )}
                  </GenerationOptions>
                </GenerationSummary>
              ) : message.versionId &&
                message.versionLabel &&
                onSelectVersion ? (
                <>
                  Done! I've created{' '}
                  <VersionLink
                    onClick={() => onSelectVersion(message.versionId!)}
                  >
                    {message.versionLabel}
                  </VersionLink>
                  . Let me know if you'd like any other changes.
                </>
              ) : (
                message.content
              )}
            </AssistantMessage>
          ),
        )}

        {isLoading && (
          <LoadingIndicator>
            <RotatingSquare strokeColor="#a0a0a0" />
            <span>
              Arbor is updating the content
              <span className="loading-dots" />
            </span>
          </LoadingIndicator>
        )}

        <div ref={messagesEndRef} />
      </MessagesContainer>

      <InputContainer onSubmit={handleSubmit}>
        <TextArea
          ref={textareaRef}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder="Type your revision request..."
          disabled={isLoading}
          data-testid="article-chat-input"
        />
        <InputActions>
          <VoiceDropdownContainer
            ref={dropdownRef}
            data-testid="article-chat-voice-dropdown"
          >
            <VoiceDropdownTrigger
              type="button"
              onClick={() =>
                !isLoading && setIsVoiceDropdownOpen(!isVoiceDropdownOpen)
              }
              disabled={isLoading}
              $isOpen={isVoiceDropdownOpen}
            >
              <VoiceIcon>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 2C10.3431 2 9 3.34315 9 5V12C9 13.6569 10.3431 15 12 15C13.6569 15 15 13.6569 15 12V5C15 3.34315 13.6569 2 12 2Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M19 10V12C19 15.866 15.866 19 12 19C8.13401 19 5 15.866 5 12V10"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 19V22"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </VoiceIcon>
              <VoiceLabel>
                {selectedVoice === 'brand'
                  ? 'Brand Voice'
                  : (selectedVoice as Speaker).name ||
                    (selectedVoice as Speaker).speakerLabel}
              </VoiceLabel>
              <DropdownArrow $isOpen={isVoiceDropdownOpen}>
                <svg
                  width="10"
                  height="6"
                  viewBox="0 0 10 6"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1 1L5 5L9 1"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </DropdownArrow>
            </VoiceDropdownTrigger>
            {isVoiceDropdownOpen && (
              <VoiceDropdownMenu>
                <VoiceDropdownItem
                  $isSelected={selectedVoice === 'brand'}
                  onClick={() => {
                    setSelectedVoice('brand');
                    setHasUserSelectedVoice(true);
                    setIsVoiceDropdownOpen(false);
                  }}
                >
                  <VoiceItemIcon $isSelected={selectedVoice === 'brand'}>
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </VoiceItemIcon>
                  <VoiceItemContent>
                    <VoiceItemName>Brand Voice</VoiceItemName>
                    <VoiceItemDescription>
                      Default organization tone
                    </VoiceItemDescription>
                  </VoiceItemContent>
                  {selectedVoice === 'brand' && <CheckIcon />}
                </VoiceDropdownItem>
                {speakers.length > 0 && <DropdownDivider />}
                {speakers.map(speaker => (
                  <VoiceDropdownItem
                    key={speaker.speakerLabel}
                    $isSelected={
                      selectedVoice !== 'brand' &&
                      (selectedVoice as Speaker).speakerLabel ===
                        speaker.speakerLabel
                    }
                    onClick={() => {
                      setSelectedVoice(speaker);
                      setHasUserSelectedVoice(true);
                      setIsVoiceDropdownOpen(false);
                    }}
                  >
                    <VoiceItemIcon
                      $isSelected={
                        selectedVoice !== 'brand' &&
                        (selectedVoice as Speaker).speakerLabel ===
                          speaker.speakerLabel
                      }
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </VoiceItemIcon>
                    <VoiceItemContent>
                      <VoiceItemName>
                        {speaker.name || speaker.speakerLabel}
                      </VoiceItemName>
                      {speaker.title && (
                        <VoiceItemDescription>
                          {speaker.title}
                        </VoiceItemDescription>
                      )}
                    </VoiceItemContent>
                    {selectedVoice !== 'brand' &&
                      (selectedVoice as Speaker).speakerLabel ===
                        speaker.speakerLabel && <CheckIcon />}
                  </VoiceDropdownItem>
                ))}
              </VoiceDropdownMenu>
            )}
          </VoiceDropdownContainer>
          <SendButton
            type="submit"
            disabled={(!inputValue.trim() && !hasVoiceChanged) || isLoading}
            data-testid="article-chat-send-button"
          >
            Send
          </SendButton>
        </InputActions>
      </InputContainer>
    </Container>
  );
};

export default ChatInterface;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: var(--surface);
  border-radius: 8px;
  border: 1px solid var(--border);
  overflow: hidden;
`;

const Header = styled.div`
  padding: 16px;
  border-bottom: 1px solid var(--border);
`;

const HeaderTitle = styled.h3`
  color: var(--accent);
  font-size: 12px;
  letter-spacing: 0.5px;
  font-weight: 500;
  margin: 0 0 4px 0;
`;

const HeaderSubtitle = styled.h4`
  color: var(--foreground);
  font-size: 16px;
  font-weight: 700;
  margin: 0;
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SuggestionsHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--border);
  margin-bottom: 8px;
`;

const SuggestionsText = styled.p`
  color: var(--text-secondary);
  font-size: 13px;
  margin: 0;
  line-height: 1.5;
`;

const WordCountBadge = styled.div`
  font-size: 11px;
  color: var(--text-muted);
  font-weight: 500;
`;

const SuggestionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Suggestion = styled.div`
  color: #f2f3f5;
  font-size: 12px;
  padding-left: 8px;
  border-left: 2px solid var(--border);
  cursor: pointer;
  transition: opacity 150ms ease;

  &:hover {
    opacity: 0.7;
  }
`;

const UserMessageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
`;

const MessageBubble = styled.div`
  padding: 10px 14px;
  border-radius: 12px;
  max-width: 90%;
  font-size: 13px;
  line-height: 1.4;
  word-wrap: break-word;
  background-color: var(--popover);
  color: var(--text-secondary);
  align-self: flex-end;
  border-bottom-right-radius: 4px;
`;

const UserMessageVersionLink = styled.span`
  font-size: 11px;
  color: var(--text-muted);
  cursor: pointer;

  &:hover {
    color: var(--text-secondary);
    text-decoration: underline;
  }
`;

const AssistantMessage = styled.div`
  padding: 8px 0;
  max-width: 100%;
  font-size: 13px;
  line-height: 1.5;
  color: var(--text-secondary);
  align-self: flex-start;
`;

const VersionLink = styled.span`
  color: var(--text-secondary);
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
  text-decoration-style: dotted;
  text-underline-offset: 2px;

  &:hover {
    text-decoration-style: solid;
  }
`;

const GenerationSummary = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const InitialGenerationMessage = styled.div`
  font-size: 13px;
  line-height: 1.5;
  color: var(--text-secondary);
`;

const GenerationOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background: var(--sidebar-hover);
  border-radius: 8px;
  border-left: 3px solid var(--primary);
`;

const OptionItem = styled.div`
  display: flex;
  gap: 8px;
  font-size: 12px;
  line-height: 1.4;
`;

const OptionLabel = styled.span`
  color: var(--text-secondary);
  font-weight: 500;
  min-width: 80px;
`;

const OptionValue = styled.span`
  color: var(--text-secondary);
  font-weight: 400;
`;

const LoadingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-secondary);
  font-size: 13px;
  padding: 8px;

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  svg {
    width: 16px;
    height: 16px;
    animation: spin 1.5s linear infinite;
  }

  @keyframes loading-dots {
    0% {
      content: '';
    }
    25% {
      content: '.';
    }
    50% {
      content: '..';
    }
    75% {
      content: '...';
    }
  }

  .loading-dots::after {
    content: '';
    animation: loading-dots 1.5s steps(1) infinite;
  }
`;

const InputContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border-top: 1px solid var(--border);
  background-color: var(--background);
`;

const TextArea = styled.textarea`
  flex: 1;
  box-sizing: border-box;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background-color: transparent;
  color: var(--text-secondary);
  font-size: 13px;
  line-height: 1.4;
  resize: none;
  height: 40px;
  min-height: 40px;
  max-height: 120px;
  overflow-y: auto;
  font-family: inherit;

  &::placeholder {
    color: var(--text-muted);
  }

  &:focus {
    outline: none;
    border-color: var(--primary);
  }

  &:disabled {
    opacity: 0.6;
  }
`;

const InputActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const VoiceDropdownContainer = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
  max-width: 200px;
`;

const VoiceDropdownTrigger = styled.button<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid
    ${({ $isOpen }) => ($isOpen ? 'var(--primary)' : 'var(--border)')};
  background-color: ${({ $isOpen }) =>
    $isOpen ? 'var(--surface-elevated)' : 'var(--surface)'};
  color: var(--text-secondary);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    border-color: ${({ $isOpen }) =>
      $isOpen ? 'var(--primary)' : 'var(--text-muted)'};
    background-color: var(--surface-elevated);
  }

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 2px var(--primary-tint);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const VoiceIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary);
`;

const VoiceLabel = styled.span`
  flex: 1;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DropdownArrow = styled.span<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  transition: transform 0.2s ease;
  transform: ${({ $isOpen }) => ($isOpen ? 'rotate(180deg)' : 'rotate(0)')};
`;

const VoiceDropdownMenu = styled.div`
  position: absolute;
  bottom: 100%;
  left: 0;
  right: 0;
  margin-bottom: 4px;
  background-color: var(--surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--elevation-md);
  overflow: hidden;
  z-index: 100;
  animation: slideUp 0.15s ease-out;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const VoiceDropdownItem = styled.div<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  cursor: pointer;
  transition: background-color 0.15s ease;
  background-color: ${({ $isSelected }) =>
    $isSelected ? 'var(--primary-tint-light)' : 'transparent'};

  &:hover {
    background-color: ${({ $isSelected }) =>
      $isSelected ? 'var(--primary-tint)' : 'var(--sidebar-hover)'};
  }
`;

const VoiceItemIcon = styled.div<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background-color: ${({ $isSelected }) =>
    $isSelected ? 'var(--primary-glow)' : 'var(--sidebar-hover)'};
  color: ${({ $isSelected }) =>
    $isSelected ? 'var(--primary)' : 'var(--text-secondary)'};
  transition: all 0.15s ease;
`;

const VoiceItemContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const VoiceItemName = styled.div`
  font-size: 13px;
  font-weight: 500;
  line-height: 1.2;
  color: var(--text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const VoiceItemDescription = styled.div`
  font-size: 11px;
  line-height: 1.2;
  color: var(--text-muted);
  margin-top: 1px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DropdownDivider = styled.div`
  height: 1px;
  background-color: var(--border);
  margin: 4px 0;
`;

const CheckIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ color: 'var(--primary)' }}
  >
    <path
      d="M20 6L9 17L4 12"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SendButton = styled.button`
  padding: 10px 16px;
  border-radius: 8px;
  border: none;
  background-color: var(--primary);
  color: var(--background);
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background-color: var(--primary-hover);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
